/**
 * Lane C0-TRUEPX-GAME: the true-pixel clean-up where the game makes its art (never per frame drawn), behind the
 * `truePx` setting (truePx.setting.ts). Main thread; the flags are in truepx/flags.ts, the workers use truepx/clean.ts directly.
 *
 * - Scenery and buildings (terrain.ts `finishProps`, `buildingLook`; passes/kitProps.ts): `truePxImg` once per
 *   picture, cached, inside the scenery's own art budget (propStream.ts `timed`); the turned pieces (render/turned/)
 *   are cut from these pictures, so they come out clean too.
 * - Ground chunks (terrain.ts `paintSome`): in place on the painted pixels before they go on the canvas, on whichever
 *   thread painted them.
 * - Figure frames (rig/finish.ts) and beasts (pixel.ts `toFigureGrid`): in place, as they are made, mostly in the
 *   figure workers. Fixed per page load (see flags.ts).
 */
import { settings } from '../../settings';
import { artX, artY, atRes, canvas, ctx2d, resOf } from '../pixel';
import type { Sprite } from '../scenery';
import { cleanSprite } from './clean';
import { registerTruePxSource, truePxScene } from './flags';

// The saved choice, read when first asked for (never while the settings are still being loaded).
registerTruePxSource(() => settings.truePx);

/** Clean-up time spent on scenery so far, for measuring (ms, pictures). */
export const truePxStats = { ms: 0, n: 0 };

const DONE = new WeakMap<HTMLCanvasElement, HTMLCanvasElement>();
const MADE = new WeakSet<HTMLCanvasElement>();

/** A scenery picture cleaned (itself when the setting is off); same size and anchors; cached per picture. */
export function truePxImg(c: HTMLCanvasElement): HTMLCanvasElement {
  if (!truePxScene() || MADE.has(c) || c.width * c.height === 0) return c;
  let r = DONE.get(c);
  if (!r) {
    const t0 = performance.now();
    const { width: w, height: h } = c;
    const img = ctx2d(c).getImageData(0, 0, w, h);
    cleanSprite(new Uint32Array(img.data.buffer), w, h, resOf(c), true);
    r = atRes(canvas(w, h), resOf(c), artX(c), artY(c));
    ctx2d(r).putImageData(img, 0, 0);
    DONE.set(c, r);
    MADE.add(r);
    truePxStats.ms += performance.now() - t0;
    truePxStats.n++;
  }
  return r;
}

/** A building or tree sprite cleaned, anchors kept. */
export function truePxSprite<T extends Sprite>(sp: T): T {
  const img = truePxImg(sp.img);
  return img === sp.img ? sp : { ...sp, img };
}

declare global {
  interface Window {
    __truePx?: typeof truePxStats;
  }
}
if (typeof window !== 'undefined') window.__truePx = truePxStats;
